/**
 * One pending invite password per person (shared across tabs via localStorage).
 * Communication → Send Emails and Participant Edit must show/send the same value.
 */
const STORAGE_KEY = 'ems_invite_plain_passwords';

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const DIGIT = '0123456789';
const SPECIAL = '@#$!';
const ALL = UPPER + LOWER + DIGIT + SPECIAL;

function pick(chars) {
  return chars.charAt(Math.floor(Math.random() * chars.length));
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Always includes upper, lower, digit, and special (@#$!). */
export function generateInvitePassword(length = 10) {
  const len = Math.max(Number(length) || 10, 4);
  const required = [pick(UPPER), pick(LOWER), pick(DIGIT), pick(SPECIAL)];
  const rest = Array.from({ length: len - 4 }, () => pick(ALL));
  return shuffle([...required, ...rest]).join('');
}

export function passwordHasSpecial(password) {
  return typeof password === 'string' && /[@#$!]/.test(password);
}

export function normalizePersonId(personId) {
  if (personId == null || personId === '') return '';
  if (typeof personId === 'object') {
    if (personId._id != null) return String(personId._id);
    if (typeof personId.toHexString === 'function') return personId.toHexString();
    const asString = String(personId);
    return asString === '[object Object]' ? '' : asString;
  }
  return String(personId);
}

function readMap() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    // Migrate older sessionStorage cache (was per-tab only)
    if (!raw) {
      const fromSession = sessionStorage.getItem(STORAGE_KEY);
      if (fromSession) {
        localStorage.setItem(STORAGE_KEY, fromSession);
        sessionStorage.removeItem(STORAGE_KEY);
        raw = fromSession;
      }
    }
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getCachedInvitePassword(personId) {
  const id = normalizePersonId(personId);
  if (!id) return '';
  const map = readMap();
  return map[id] || '';
}

export function setCachedInvitePassword(personId, password) {
  const id = normalizePersonId(personId);
  if (!id || !password) return password || '';
  const map = readMap();
  map[id] = password;
  writeMap(map);
  return password;
}

/** Reuse cached password if it has a special char; otherwise create a strong one. */
export function getOrCreateInvitePassword(personId, generateFn = generateInvitePassword) {
  const id = normalizePersonId(personId);
  const create = typeof generateFn === 'function' ? generateFn : generateInvitePassword;
  if (!id) return create();

  const existing = getCachedInvitePassword(id);
  if (existing && passwordHasSpecial(existing)) return existing;

  const created = create();
  return setCachedInvitePassword(id, created);
}
